/**
 * Data layer for the COMPANY panel's Games section.
 *
 * THE FOUR GAMES DO NOT SHARE A UNIT OF PLAY, and nothing in this file pretends
 * they do. A chess game, a word-search level, a block-fill level and a quiz
 * attempt are different events taking different amounts of time, so every
 * payload carries its own `unit` string and the adapters below keep it. Where a
 * combined total is shown it is labelled as one, and `playsAreComplete` says
 * whether a withheld game is missing from it.
 *
 * NO LEADERBOARD, AND NO SCORE BESIDE A NAME. The app has its own leaderboards
 * that players see and can opt out of; republishing one in the employer's panel
 * is a different act. Scores, win rates, ranks and levels reached arrive here
 * only as population distributions. The users fetcher returns plays, days,
 * dates and which games — nothing else — and there is no per-person fetcher
 * because there is no per-person endpoint.
 *
 * `isUserGamePrivate` IS HONOURED. Anyone who set it is absent from the users
 * list and counted in `hiddenCount` instead, so the table can say it is showing
 * fewer people than the department holds rather than quietly under-reporting.
 *
 * SUPPRESSION HAPPENS AT TWO LEVELS. The whole section returns
 * `{ suppressed: true, minimum }` under the backend's minimum, and separately a
 * single game with too few players is withheld from the overview's breakdown
 * (listed in `withheld`) and returns its own suppressed payload on its own tab.
 * Pages must check `suppressed` before reading anything else. Inside a reported
 * page, individual table cells can still be withheld — rates arrive as null
 * when too few plays sit behind them, which is why the tables render a dash
 * rather than a zero.
 *
 * See Backend services/gamesHubService.js for the full reasoning, including the
 * list of dead game collections it deliberately does not read.
 *
 * Backed by the company/v1 games-hub endpoints (token-scoped to the company;
 * the B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes
 * an optional `department` so the section's filter narrows the whole page.
 */

import {
  getCompanyGamesDashboard,
  getCompanyGameDetail,
  getCompanyGamesUsers
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/**
 * What a failed request becomes.
 *
 * `GetApi` swallows network errors and returns undefined, and `unwrap` then
 * throws — so without this every fetcher rejects and the page dies in an
 * uncaught-error overlay the moment the API is unreachable (a restarting
 * backend, an expired token, a 500). A section that cannot load should say so,
 * not crash the panel around it, and "failed" must stay distinguishable from
 * "suppressed" and from "nobody has played anything".
 */
const failedShape = () => ({ error: true, suppressed: false });

const suppressedShape = (data) => ({
  suppressed: true,
  minimum: data.minimum ?? 0,
  totalUsers: data.totalUsers ?? 0,
  key: data.key ?? null,
  label: data.label ?? null
});

const list = (rows) => (Array.isArray(rows) ? rows : []);

/** Cross-game overview. */
const adaptOverview = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    suppressed: false,
    minimum: data.minimum ?? 0,

    // Reach — the two figures that mean the same thing in all four games.
    totalUsers: data.totalUsers ?? 0,
    players: data.players ?? 0,
    adoption: data.adoption ?? 0,

    // Volume — reportable games only; see playsAreComplete.
    totalPlays: data.totalPlays ?? 0,
    recentPlays: data.recentPlays ?? 0,
    playsAreComplete: data.playsAreComplete !== false,

    gamesAvailable: data.gamesAvailable ?? 0,
    gamesReported: data.gamesReported ?? 0,
    averageGamesTried: data.averageGamesTried ?? 0,

    breadth: list(data.breadth),
    games: list(data.games),
    playSplit: list(data.playSplit),
    playerSplit: list(data.playerSplit),
    weekday: list(data.weekday),

    trend: data.trend || {},
    trendKeys: list(data.trendKeys),

    returnRate: data.returnRate ?? null,
    returnBase: data.returnBase ?? 0,

    // Games hidden for being too small, and games nobody has opened at all.
    // Different facts, kept apart: one is withheld, the other is unused.
    withheld: list(data.withheld),
    untouched: list(data.untouched)
  };
};

/**
 * One game's page. The shape differs per game by design — chess has a win rate,
 * the quiz has an accuracy, word search has a score — so the adapter keeps
 * whatever the builder sent and only guarantees the fields every game shares.
 */
const adaptGame = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    ...data,
    suppressed: false,
    plays: data.plays ?? 0,
    players: data.players ?? 0,
    activeDays: data.activeDays ?? 0,
    playsPerPlayer: data.playsPerPlayer ?? 0,
    totalUsers: data.totalUsers ?? 0,
    trend: list(data.trend),
    weekday: list(data.weekday)
  };
};

export const fetchCompanyGamesOverview = (department = '') =>
  getCompanyGamesDashboard(department)
    .then((res) => adaptOverview(unwrap(res) || {}))
    .catch(() => failedShape());

export const fetchCompanyGameDetail = (gameKey, department = '') =>
  getCompanyGameDetail(gameKey, department)
    .then((res) => adaptGame(unwrap(res) || {}))
    .catch(() => failedShape());

export const fetchCompanyGamesUsers = ({ page, perPage, search, department }) =>
  getCompanyGamesUsers({ page, perPage, search, department }).then((res) => {
    const data = unwrap(res) || {};
    return {
      data: list(data.data),
      totalCount: data.totalCount ?? 0,
      // People who opted out of being listed. Shown as a count so the table is
      // never silently short of the department it claims to cover.
      hiddenCount: data.hiddenCount ?? 0,
      games: list(data.games)
    };
  });
